import {
  buildCartMergeKey,
  getCartLineTotal,
  normalizeCartLineAddOns,
} from '../pages/OurMenu/cartAddOnUtils';

export const SUBSCRIPTION_CART_SLOT_OPTIONS = [
  { id: 'breakfast', label: 'Breakfast' },
  { id: 'lunch', label: 'Lunch' },
  { id: 'dinner', label: 'Dinner' },
];

export const SUBSCRIPTION_CART_KIND = {
  alacarte: 'alacarte',
  specialty: 'specialty',
  fullDay: 'full_day',
};

/** Display name when a subscription is built only from Our Menu dishes. */
export const OUR_MENU_SELECTION_LABEL = 'Our menu';

const SLOT_IDS = new Set(SUBSCRIPTION_CART_SLOT_OPTIONS.map((slot) => slot.id));

const DEFAULT_SLOT_ID = 'lunch';

/** Mid-morning folds into breakfast; evening snack folds into dinner. */
export function canonicalizePlanSlotId(slotId) {
  const code = String(slotId || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (code === 'mid_morning' || code === 'midmorning' || code === 'mid_mor_snack') {
    return 'breakfast';
  }
  if (code === 'evening_snack' || code === 'evening' || code === 'evng_snack' || code === 'snacks') {
    return 'dinner';
  }
  if (SLOT_IDS.has(code)) return code;
  return '';
}

/**
 * Infer a delivery slot for an Our Menu / cart line so it can be added as
 * Regular “Our menu” (à la carte) on a subscription.
 * Kitchen windows are Breakfast, Lunch, and Dinner only.
 */
export function suggestSlotIdForMenuItem(item) {
  const haystack = `${item?.category || ''} ${item?.name || ''} ${item?.menuName || ''}`.toLowerCase();

  if (/(breakfast|dosa|idli|idly|upma|pesarattu|tiffin|poha)/.test(haystack)) {
    return 'breakfast';
  }
  // Drinks / light bites that used to be mid-morning now ride with breakfast.
  if (/(drink|beverage|juice|detox|java|lassi|buttermilk)/.test(haystack)) {
    return 'breakfast';
  }
  if (/(dinner|night meal)/.test(haystack)) {
    return 'dinner';
  }
  // Snacks that used to be evening snack now ride with dinner.
  if (/(evening|snack|samosa|bajji|pakora|bakery)/.test(haystack) && !/thali|biryani|rice/.test(haystack)) {
    return 'dinner';
  }
  if (/(thali|rice|biryani|bread|chapathi|chapati|paratha|roti|naan|lunch|curry)/.test(haystack)) {
    return 'lunch';
  }
  return DEFAULT_SLOT_ID;
}

/**
 * Whether an Our Menu dish belongs in this builder slot.
 * Lunch and dinner share mains; breakfast keeps tiffins and drinks.
 */
export function isMenuItemRelevantToSlot(item, slotId) {
  const slot = canonicalizePlanSlotId(slotId) || String(slotId || '').trim().toLowerCase();
  if (!slot) return false;
  const suggested = suggestSlotIdForMenuItem(item);
  if (suggested === slot) return true;
  if (slot === 'dinner' && suggested === 'lunch') return true;
  if (slot === 'lunch' && suggested === 'dinner') return true;
  const categoryHaystack = `${item?.category || ''} ${item?.menuName || ''} ${item?.menu_name || ''}`.toLowerCase();
  if (slot === 'breakfast' && /(breakfast|tiffin|idli|dosa|drink|beverage)/.test(categoryHaystack)) {
    return true;
  }
  if ((slot === 'lunch' || slot === 'dinner') && /(lunch|dinner|thali|rice|bread|meal|curry|main)/.test(categoryHaystack)) {
    return true;
  }
  return false;
}

export function normalizeSubscriptionCartSlotId(slotId, item) {
  const kind = item ? resolveCartItemKind(item) : SUBSCRIPTION_CART_KIND.alacarte;
  if (kind === SUBSCRIPTION_CART_KIND.specialty || kind === SUBSCRIPTION_CART_KIND.fullDay) {
    const code = String(slotId || '').trim();
    return code || suggestSlotIdForMenuItem(item);
  }
  return canonicalizePlanSlotId(slotId) || suggestSlotIdForMenuItem(item);
}

export function resolveCartItemKind(item) {
  if (!item) return SUBSCRIPTION_CART_KIND.alacarte;
  if (
    item.kind === SUBSCRIPTION_CART_KIND.fullDay
    || item.source === SUBSCRIPTION_CART_KIND.fullDay
    || item.isFullDayPackage
    || String(item.category || '') === 'Full Day'
  ) {
    return SUBSCRIPTION_CART_KIND.fullDay;
  }
  if (
    item.kind === SUBSCRIPTION_CART_KIND.specialty
    || item.source === SUBSCRIPTION_CART_KIND.specialty
    || item.planId
    || String(item.category || '') === 'Specialty plan'
  ) {
    return SUBSCRIPTION_CART_KIND.specialty;
  }
  return SUBSCRIPTION_CART_KIND.alacarte;
}

export function isAlacarteDraftItem(item) {
  if (!item || item.isFullDayPackage) return false;
  if (item.planId) return false;
  const category = String(item.category || '');
  if (category === 'Specialty plan' || category === 'Plan Base' || category === 'Full Day') return false;
  return resolveCartItemKind(item) === SUBSCRIPTION_CART_KIND.alacarte;
}

/** True when every selected line is Regular Our menu (no specialty / Full Day). */
export function isOurMenuOnlyProductList(products = []) {
  const list = (Array.isArray(products) ? products : []).filter(Boolean);
  if (!list.length) return false;
  return list.every(isAlacarteDraftItem);
}

export function isSpecialtyDraftItem(item) {
  if (!item || item.isFullDayPackage) return false;
  return Boolean(item.planId) || resolveCartItemKind(item) === SUBSCRIPTION_CART_KIND.specialty;
}

export function subscriptionCartLineKey(item) {
  const kind = resolveCartItemKind(item);
  if (kind === SUBSCRIPTION_CART_KIND.fullDay) {
    return `full_day:${item?.fullDayGroupId || item?.id}`;
  }
  const slotId = normalizeSubscriptionCartSlotId(item?.slotId, item);
  if (kind === SUBSCRIPTION_CART_KIND.specialty) {
    return `specialty:${item?.id}::${slotId}`;
  }
  const hostAndAddOns = buildCartMergeKey(item?.id, item?.addOns);
  return `alacarte:${hostAndAddOns}::${slotId}`;
}

/** Line money for plan cart (host × qty + independent add-ons). */
export function getSubscriptionCartLineTotal(item) {
  const kind = resolveCartItemKind(item);
  if (kind !== SUBSCRIPTION_CART_KIND.alacarte) {
    return toMoney(item?.price) * toQty(item?.quantity);
  }
  return getCartLineTotal(item);
}

function toMoney(value) {
  const price = Number(value);
  return Number.isFinite(price) ? price : 0;
}

function toQty(value) {
  return Math.max(1, Math.round(Number(value) || 1));
}

function snapshotSlotProduct(product, slotId, groupId) {
  return {
    id: product.id,
    name: product.name,
    price: toMoney(product.price),
    planId: product.planId || null,
    isOrganic: Boolean(product.isOrganic),
    regionKey: product.regionKey || null,
    category: product.category || 'Specialty plan',
    includedInPlan: Boolean(product.includedInPlan),
    quantity: toQty(product.quantity),
    slotId,
    isFullDayPackage: true,
    fullDayGroupId: groupId,
  };
}

export function toAlacarteSlotDraftProduct(item) {
  const slotId = normalizeSubscriptionCartSlotId(item?.slotId, item);
  const addOns = normalizeCartLineAddOns(item.addOns);
  const hostUnitPrice =
    item.hostUnitPrice != null && Number.isFinite(Number(item.hostUnitPrice))
      ? Number(item.hostUnitPrice)
      : toMoney(item.price);
  return {
    id: item.id,
    name: item.name,
    price: hostUnitPrice,
    hostUnitPrice,
    priceDisplay: item.priceDisplay || `₹${hostUnitPrice.toFixed(2)}`,
    image: item.image,
    category: item.category || 'Our menu',
    quantity: toQty(item.quantity),
    slotId,
    isOrganic: false,
    source: SUBSCRIPTION_CART_KIND.alacarte,
    kind: SUBSCRIPTION_CART_KIND.alacarte,
    includedInPlan: false,
    variantLabel: item.variantLabel || undefined,
    parentItemId: item.parentItemId || undefined,
    addOns,
  };
}

export function toSpecialtySlotDraftProduct(item) {
  const slotId = normalizeSubscriptionCartSlotId(item?.slotId, item);
  return {
    id: item.id,
    name: item.name,
    price: toMoney(item.price),
    planId: item.planId,
    isOrganic: Boolean(item.isOrganic),
    ...(item.regionKey ? { regionKey: item.regionKey } : {}),
    category: item.category || 'Specialty plan',
    includedInPlan: Boolean(item.includedInPlan),
    quantity: toQty(item.quantity),
    slotId,
    isFullDayPackage: false,
    source: SUBSCRIPTION_CART_KIND.specialty,
    kind: SUBSCRIPTION_CART_KIND.specialty,
  };
}

export function normalizeSubscriptionCartItem(item, { quantity, slotId } = {}) {
  const kind = resolveCartItemKind(item);
  const qty = toQty(quantity ?? item?.quantity);

  if (kind === SUBSCRIPTION_CART_KIND.fullDay) {
    const fullDayGroupId = String(item.fullDayGroupId || item.id || '');
    const slotProducts = (Array.isArray(item.slotProducts) ? item.slotProducts : [])
      .map((product) => snapshotSlotProduct(product, product.slotId, fullDayGroupId));
    const price = slotProducts.reduce((sum, product) => sum + toMoney(product.price), 0);
    const planName = String(item.name || 'Specialty').replace(/\s*full day\s*$/i, '').trim();
    return {
      kind,
      source: kind,
      id: fullDayGroupId,
      fullDayGroupId,
      planId: item.planId || slotProducts[0]?.planId || null,
      name: item.name || `${planName} Full Day`,
      price: toMoney(item.price) || price,
      priceDisplay: item.priceDisplay,
      isOrganic: Boolean(item.isOrganic),
      regionKey: item.regionKey || slotProducts[0]?.regionKey || null,
      quantity: qty,
      category: 'Full Day',
      slotIds: slotProducts.map((product) => product.slotId),
      slotProducts,
    };
  }

  if (kind === SUBSCRIPTION_CART_KIND.specialty) {
    const resolvedSlotId = normalizeSubscriptionCartSlotId(slotId || item?.slotId, item);
    const price = toMoney(item.price);
    return {
      kind,
      source: kind,
      id: item.id,
      name: item.name,
      price,
      priceDisplay: item.priceDisplay || `₹${price.toFixed(2)}`,
      planId: item.planId || null,
      slotId: resolvedSlotId,
      isOrganic: Boolean(item.isOrganic),
      regionKey: item.regionKey || null,
      quantity: qty,
      category: item.category || 'Specialty plan',
      includedInPlan: Boolean(item.includedInPlan),
    };
  }

  const resolvedSlotId = normalizeSubscriptionCartSlotId(slotId || item?.slotId, item);
  const addOns = normalizeCartLineAddOns(item?.addOns);
  const hostUnitPrice =
    item?.hostUnitPrice != null && Number.isFinite(Number(item.hostUnitPrice))
      ? Number(item.hostUnitPrice)
      : toMoney(item?.price);
  return {
    kind: SUBSCRIPTION_CART_KIND.alacarte,
    source: SUBSCRIPTION_CART_KIND.alacarte,
    id: item.id,
    name: item.name,
    price: hostUnitPrice,
    hostUnitPrice,
    priceDisplay: item.priceDisplay || `₹${hostUnitPrice.toFixed(2)}`,
    image: item.image,
    category: item.category || 'Our menu',
    quantity: qty,
    slotId: resolvedSlotId,
    isOrganic: false,
    variantLabel: item.variantLabel || undefined,
    parentItemId: item.parentItemId || undefined,
    addOns,
  };
}

/** Expand one plan-cart line into builder slot-draft products. */
export function expandCartItemToDraftProducts(item) {
  const normalized = normalizeSubscriptionCartItem(item);
  if (normalized.kind === SUBSCRIPTION_CART_KIND.fullDay) {
    return (normalized.slotProducts || []).map((product) => ({
      slotId: product.slotId,
      product: {
        ...product,
        quantity: normalized.quantity,
        isFullDayPackage: true,
        fullDayGroupId: normalized.fullDayGroupId,
      },
    }));
  }
  if (normalized.kind === SUBSCRIPTION_CART_KIND.specialty) {
    return [{ slotId: normalized.slotId, product: toSpecialtySlotDraftProduct(normalized) }];
  }
  return [{ slotId: normalized.slotId, product: toAlacarteSlotDraftProduct(normalized) }];
}

/**
 * Rebuild builder drafts from the plan cart (Our menu, specialty slots, and Full Day).
 */
export function applySubscriptionCartToSlotDrafts(prev = {}, cartItems = []) {
  const next = {};
  const slotIds = new Set([
    ...Object.keys(prev || {}),
    ...SUBSCRIPTION_CART_SLOT_OPTIONS.map((slot) => slot.id),
  ]);

  slotIds.forEach((slotId) => {
    next[slotId] = [];
  });

  (Array.isArray(cartItems) ? cartItems : []).forEach((item) => {
    expandCartItemToDraftProducts(item).forEach(({ slotId, product }) => {
      const destSlotId = product.isFullDayPackage || product.planId
        ? slotId
        : (canonicalizePlanSlotId(slotId) || slotId);
      if (!next[destSlotId]) next[destSlotId] = [];
      next[destSlotId].push({ ...product, slotId: destSlotId });
    });
  });

  return next;
}

export function slotLabelForId(slotId) {
  return SUBSCRIPTION_CART_SLOT_OPTIONS.find((slot) => slot.id === slotId)?.label || slotId;
}

export function cartItemKindLabel(item) {
  const kind = resolveCartItemKind(item);
  if (kind === SUBSCRIPTION_CART_KIND.fullDay) return 'Full Day';
  if (kind === SUBSCRIPTION_CART_KIND.specialty) return 'Specialty';
  return OUR_MENU_SELECTION_LABEL;
}

export function cartItemDietLabel(item) {
  return item?.isOrganic ? 'Organic' : 'Regular';
}

export function planMetaFromCartItems(cartItems = []) {
  const found = [...(Array.isArray(cartItems) ? cartItems : [])]
    .reverse()
    .find((item) => item?.planId);
  if (!found) return null;
  return {
    planId: found.planId,
    isOrganic: Boolean(found.isOrganic),
    regionKey: found.regionKey || null,
  };
}

export function canChangeSubscriptionCartSlot(item) {
  return resolveCartItemKind(item) === SUBSCRIPTION_CART_KIND.alacarte;
}
